import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Dialog, DialogContent, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { rf } from "./theme";
import { Display, Pill, PushButton } from "./ui";
import { USING_MOCKS } from "./api/client";
import { useJoinWaitlist, useRequestCallback } from "./api/hooks";
import EnrollFlow from "./EnrollFlow";


const emptyForm = { name: "", email: "", phone: "" };

const fieldSx = {
  backgroundColor: rf.paper,
  "& .MuiOutlinedInput-root": {
    borderRadius: rf.radiusSm,
    fontFamily: rf.body,
    "& fieldset": { border: rf.lineThin },
    "&:hover fieldset": { border: rf.lineThin },
    "&.Mui-focused fieldset": { border: rf.line, borderColor: rf.coral },
  },
  "& label": { fontFamily: rf.body },
  // No reserved line under each field: the gap only appears when there is
  // something to say.
  "& .MuiFormHelperText-root": { fontFamily: rf.body, marginLeft: 0, marginTop: "6px" },
};

// One dialog, three jobs: joining a cohort (Google sign-in then payment, in
// EnrollFlow), joining a waitlist, and asking for a call back. The
// "ask" mode deliberately shows no cohort, no syllabus and no price — someone
// who is not ready to buy should not be handed a checkout. Name and number is
// all anyone needs to ring you back.
export const CohortDialog = ({ open, mode, cohort, onClose }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(null);

  const waitlist = useJoinWaitlist();
  const callback = useRequestCallback();
  const pending = waitlist.isPending || callback.isPending;
  const failed = waitlist.isError || callback.isError;

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setErrors({});
      setDone(null);
      waitlist.reset();
      callback.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cohort?.slug, mode]);

  const asking = mode === "ask";
  if (!cohort && !asking) return null;

  const enrolling = mode === "enroll";

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = asking ? "We'll need something to call you." : "We need a name to put against the seat.";
    if (!asking && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) next.email = "That doesn't look like an email address.";
    const digits = form.phone.replace(/[\s-]/g, "");
    if (!/^\+?\d{10,15}$/.test(digits)) next.phone = "A number we can reach you on, please.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      cohortSlug: cohort?.slug,
      name: form.name.trim(),
      phone: form.phone.replace(/[\s-]/g, ""),
      source: "reinforce-homepage",
      ...(asking ? {} : { email: form.email.trim() }),
    };

    try {
      if (asking) {
        await callback.mutateAsync({ name: payload.name, phone: payload.phone, cohortTitle: cohort?.title });
        setDone({ kind: "ask" });
      } else {
        setDone({ kind: "waitlist", ...(await waitlist.mutateAsync(payload)) });
      }
    } catch {
      // The mutation's error state renders the message below.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: rf.radius, border: rf.line, boxShadow: rf.shadowLg, backgroundColor: rf.cream } }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, backgroundColor: rf.cream }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2.5 }}>
          <Box>
            <Pill bg={enrolling ? rf.coral : asking ? rf.amber : rf.sky} sx={{ mb: 1.25, textTransform: "uppercase", fontSize: 11 }}>
              {enrolling ? "Join this cohort" : asking ? "Talk to us" : "Waitlist · next batch"}
            </Pill>
            <Display component="h2" size={26}>
              {asking ? "We'll call you back" : enrolling ? cohort.title : `${cohort.title} — next batch`}
            </Display>
          </Box>
          <Box
            onClick={onClose}
            role="button"
            aria-label="Close"
            sx={{
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              width: 34,
              height: 34,
              flexShrink: 0,
              backgroundColor: rf.paper,
              border: rf.lineThin,
              borderRadius: "10px",
              color: rf.ink,
            }}
          >
            <CloseIcon fontSize="small" />
          </Box>
        </Box>

        {enrolling ? (
          <EnrollFlow cohort={cohort} onClose={onClose} />
        ) : done ? (
          <Box>
            <Box sx={{ p: 2.5, mb: 2.5, backgroundColor: rf.mint, border: rf.line, borderRadius: rf.radiusSm, boxShadow: rf.shadowSm }}>
              <Typography sx={{ fontFamily: rf.display, fontSize: 17, fontWeight: 800, color: rf.ink, mb: 0.5 }}>
                {done.kind === "ask" ? "Got it — we'll call you" : "You're on the list"}
              </Typography>
              <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, color: rf.ink, lineHeight: 1.6 }}>
                {done.kind === "ask"
                  ? "Someone who teaches these cohorts will ring you within one working day. Ask us anything — including whether this is the wrong thing for you."
                  : "You'll hear from us the day registrations open for the next batch, before the page says so."}
              </Typography>
            </Box>
            <PushButton variant="dark" onClick={onClose}>
              Done
            </PushButton>
          </Box>
        ) : (
          <Box component="form" onSubmit={submit} noValidate>
            {!asking && (
              <Typography sx={{ fontFamily: rf.body, fontSize: 15, color: rf.inkSoft, lineHeight: 1.6, mb: 2.5 }}>
                This batch has started and registrations are closed. Leave your details and you get first word when the
                next one opens.
              </Typography>
            )}

            {asking && (
              <Typography sx={{ fontFamily: rf.body, fontSize: 15, color: rf.inkSoft, lineHeight: 1.6, mb: 2.5 }}>
                Leave a name and a number. We'll call you back and answer whatever you want to ask first — no form to
                fill in, nothing to sign up for.
              </Typography>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2.5 }}>
              {[
                { key: "name", label: "Your name", type: "text" },
                ...(asking ? [] : [{ key: "email", label: "Email", type: "email" }]),
                { key: "phone", label: "Phone", type: "tel" },
              ].map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  type={field.type}
                  value={form[field.key]}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                  error={Boolean(errors[field.key])}
                  helperText={errors[field.key] || ""}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
              ))}
            </Box>

            {failed && (
              <Box sx={{ p: 2, mb: 2, backgroundColor: rf.coral, border: rf.lineThin, borderRadius: rf.radiusSm }}>
                <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, fontWeight: 600, color: rf.ink }}>
                  That didn't go through. Try again in a moment.
                </Typography>
              </Box>
            )}

            {USING_MOCKS && !asking && (
              <Typography sx={{ fontFamily: rf.body, fontSize: 13, color: rf.inkFaint, lineHeight: 1.55, mb: 2 }}>
                Demo mode: there is no backend behind this form yet. Nothing is stored.
              </Typography>
            )}

            <PushButton type="submit" variant="primary" size="lg" fullWidth disabled={pending}>
              {pending ? (
                <CircularProgress size={22} sx={{ color: rf.ink }} />
              ) : asking ? (
                "Request a call"
              ) : (
                "Add me to the waitlist"
              )}
            </PushButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CohortDialog;
